/**
 * AdventureLogPostPage — /adventure-logs/$slug individual post.
 * Renders build-time HTML from the manifest. No runtime markdown parsing.
 */
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from '@tanstack/react-router';
import SchemaJsonLd from '../components/primitives/SchemaJsonLd.jsx';
import Reveal from '../components/ui/Reveal.jsx';
import { posts } from '../content/adventure-logs-manifest.js';
import { adventureLogsIndex } from '../content/adventureLogs.js';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr || '';
  return d.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function AdventureLogNotFound({ slug }) {
  return (
    <>
      <Helmet>
        <title>Post not found — Adventure Logs</title>
      </Helmet>
      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page max-w-prose">
          <h1 className="text-display-lg font-display font-semibold mb-4">Post not found</h1>
          <p className="text-body text-ink-200 mb-8">
            Could not find post: <code>"{slug}"</code>
          </p>
          <Link
            to="/adventure-logs"
            className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors"
          >
            {adventureLogsIndex.backLinkLabel}
          </Link>
        </div>
      </section>
    </>
  );
}

export default function AdventureLogPostPage() {
  // strict: false lets useParams read $slug from any matched route without type-narrowing to a single route ID.
  const { slug } = useParams({ strict: false });
  const post = posts.find((p) => p.slug === slug);

  if (!post) {
    if (typeof window === 'undefined') {
      console.warn(`[ssr] AdventureLogPostPage: no post found for slug "${slug}"`);
    }
    return <AdventureLogNotFound slug={slug} />;
  }

  const postUrl = post.canonical || `https://artificersai.com/adventure-logs/${post.slug}`;

  const blogPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.dateModified,
    wordCount: post.wordCount,
    keywords: post.keywords || [],
    inLanguage: 'en-AU',
    author: {
      '@type': 'Person',
      name: post.author,
      url: 'https://artificersai.com/about',
    },
    publisher: {
      '@type': 'Organization',
      name: "The Artificer's AI",
      url: 'https://artificersai.com',
    },
    url: postUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl,
    },
  };

  return (
    <>
      <Helmet>
        <title>{post.title} — Adventure Logs</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={postUrl} />
        <meta property="og:title" content={`${post.title} — Adventure Logs`} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={postUrl} />
      </Helmet>

      <SchemaJsonLd data={blogPostingSchema} />

      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page max-w-prose">
          <Reveal as="header" delay={0} className="mb-10">
            <h1 className="text-display-lg font-display font-semibold mb-4">
              {post.title}
            </h1>
            <p className="text-body text-ink-300">
              By {post.author} · {formatDate(post.date)}
            </p>
          </Reveal>

          {/* post.html is generated at build time from trusted markdown; no user input enters this path. */}
          <div
            className="post-body"
            dangerouslySetInnerHTML={{ __html: post.html }}
          />

          <Reveal as="div" delay={0} className="mt-12 pt-8 border-t border-white/[.08]">
            <Link
              to="/adventure-logs"
              className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors"
            >
              {adventureLogsIndex.backLinkLabel}
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
